import Color from 'color';

import './panel.style.css';
import { AnalyticsConfig, BrandingConfig, IntegrationConfig } from '../interfaces';
import { EventEmitter } from 'events';
import { getParentByDataAttribute, redirect } from '../triggers';
import { book_now } from '../translations.json';
import { Language } from '../app';
import { debugLog } from '../utils/debug';
import qs from 'qs';
import { getGoogleSessionParams } from '../utils/googleAnalytics';
import { removeQueryParam } from '../utils/url';

type Config = {
  branding: BrandingConfig;
  analytics: AnalyticsConfig;
  integration: IntegrationConfig;
  lng: Language;
  hotelId?: string;
};

export type DBMEvent =
  | {
      message: 'gldbm:closePanel' | 'gldbm:toggleExpandPanel';
      url: string;
      areThirdPartyCookiesEnabled?: never;
    }
  | { message: 'gldbm:thirdPartyCookies'; url: string; areThirdPartyCookiesEnabled: boolean };
export type DBMMessage = 'gldbm:closePanel' | 'gldbm:toggleExpandPanel' | 'gldbm:thirdPartyCookies';

const PREFIX = 'gldbm-panel';
const MAX_ZINDEX = '2147483646';

export function isNotChrome() {
  const userAgent = navigator.userAgent;
  const isChrome =
    userAgent.includes('Chrome') && !userAgent.includes('Edg') && !userAgent.includes('OPR');
  return !isChrome;
}

let areThirdPartyCookiesEnabled: boolean;
const handleIncomingMessage = async (event: MessageEvent<DBMEvent>) => {
  if (event.data.message === 'gldbm:closePanel') {
    hidePanel(event.data.url);
  }
  if (event.data.message === 'gldbm:toggleExpandPanel') {
    toggleExpandPanel(event.data.url);
  }

  if (event.data.message === 'gldbm:thirdPartyCookies') {
    areThirdPartyCookiesEnabled = event.data.areThirdPartyCookiesEnabled;

    const iframeToRemove = document.getElementById(`${PREFIX}__dbmIframeTestCookies`);
    if (iframeToRemove && iframeToRemove.parentNode) {
      iframeToRemove.parentNode.removeChild(iframeToRemove);
    }
  }
};

function shouldUpdatePanelUrl(url1: string, url2: string) {
  // remove 'ts' query param from both URLs (as it will always be different)
  const urlWithoutTs1 = url1 ? removeQueryParam(url1, 'ts') : url1;
  const urlWithoutTs2 = url2 ? removeQueryParam(url2, 'ts') : url2;
  const shouldUpdate = urlWithoutTs1 !== urlWithoutTs2;

  if (shouldUpdate) {
    debugLog('Panel - will be updated', { url1: urlWithoutTs1, url2: urlWithoutTs2 });
  }

  return shouldUpdate;
}

const showPanel = async (url: string, config: Config, triggerEventEmitter?: EventEmitter) => {
  const parsedUrl = new URL(url);
  const panel = document.getElementById('gldbm-panel');
  const iOSDevice = !!navigator.platform.match(/iPhone|iPod/);
  const iOSVersion = iOSDevice
    ? parseFloat(
        (navigator.userAgent.match(/\b[0-9]+_[0-9]+(?:_[0-9]+)?\b/) || [''])[0].replace(/_/g, '.')
      )
    : null;

  const isOutdatedIOS = !!iOSVersion && iOSVersion < 14;
  if (isOutdatedIOS || !areThirdPartyCookiesEnabled) {
    window.GLAppInsights.trackTrace({
      message: 'Panel - Redirected',
      properties: { dbmURL: url, iOSVersion, areThirdPartyCookiesEnabled }
    });
    let redirectBaseUrl = url;
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);

    // url input can be baseUrl or full redirect url
    if (pathSegments.length > 0) {
      redirectBaseUrl = `${parsedUrl.origin}/${pathSegments[0]}`;
    }

    const params = qs.parse(parsedUrl.search, { ignoreQueryPrefix: true });

    redirect({
      config,
      params,
      dbmBaseUrl: redirectBaseUrl,
      triggerEventEmitter,
      enabledThirdPartyCookies: false
    });
  } else if (panel) {
    let panelUrl = url;
    let panelQueryParams = {};
    const existingQueryParams = qs.parse(parsedUrl.search, { ignoreQueryPrefix: true });

    // url input can be baseUrl or full redirect url
    if (Object.entries(existingQueryParams).length === 0) {
      const otherParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
      const googleParams = config.analytics.gtmContainerId ? await getGoogleSessionParams() : {};
      panelQueryParams = {
        ...existingQueryParams,
        ...googleParams,
        ...otherParams,
        lng: config.lng,
        hotelId: config.hotelId
      };
    }

    panelUrl += panelUrl.includes('?') ? '&' : '?';
    panelUrl = `${panelUrl}${qs.stringify({
      ...panelQueryParams,
      enableCustomFrameAncestor: true
    })}`;

    document.body.classList.add('gldbm-panel__show');
    const iframe = <HTMLIFrameElement>document.getElementById(`${PREFIX}__dbmIframe`);

    debugLog('Panel - Iframe URL', iframe.src);
    debugLog('Panel - Panel URL', panelUrl);
    const shouldUpdateUrl = shouldUpdatePanelUrl(iframe.src, panelUrl);

    if (!iframe.src || shouldUpdateUrl) iframe.src = panelUrl;
    iframe.addEventListener('load', function() {
      iframe.contentWindow && iframe.contentWindow.postMessage('Panel opened', panelUrl);
    });

    debugLog('Panel - Opened', { dbmURL: panelUrl });

    window.GLAppInsights.trackTrace({
      message: 'Panel - Opened',
      properties: { dbmURL: panelUrl }
    });

    setTimeout(() => {
      panel.style.zIndex = MAX_ZINDEX;
      panel.classList.remove('hiding');
      panel.classList.toggle('active');
    }, 200);
  }
};

const hidePanel = (dbmURL: string) => {
  const panel = document.getElementById('gldbm-panel');
  const backdrop = document.getElementById('gldbm-panel__backdrop');
  const iframe = <HTMLIFrameElement>document.getElementById(`${PREFIX}__dbmIframe`);
  const container = document.getElementById(`${PREFIX}`);
  const panelMode =
    container && container.classList.contains('fullscreen') ? 'fullscreen' : 'slide';

  iframe.contentWindow && iframe.contentWindow.postMessage('Panel closed', dbmURL);

  if (panel && backdrop) {
    if (panelMode === 'fullscreen') {
      panel.classList.add('hiding');
    }

    panel.classList.remove('active');
    backdrop.classList.add('out');

    window.GLAppInsights.trackTrace({
      message: 'Panel - Closed',
      properties: { dbmURL }
    });

    setTimeout(() => {
      if (panelMode === 'fullscreen') {
        panel.style.zIndex = '-1';
      }

      backdrop.classList.remove('out');
      document.body.classList.remove('gldbm-panel__show');
    }, 600);
  }
};

const toggleExpandPanel = (dbmURL: string) => {
  const panel = document.getElementById('gldbm-panel');

  window.GLAppInsights.trackTrace({
    message: 'Panel - Expanded',
    properties: { dbmURL }
  });

  if (panel) panel.classList.toggle('expand');
};

const insertHTML = (htmlString: string, parentNode: HTMLElement): void => {
  parentNode.insertAdjacentHTML('beforeend', htmlString);
};

export default async function(
  dbmBaseUrl: string,
  collectionId: string,
  triggerEventEmitter: EventEmitter,
  config: Config
) {
  const { branding, integration, lng } = config;
  const isMobile = window.innerWidth <= 450;
  const primaryColour = Color(branding.palettePrimary);
  const primaryTextColour = primaryColour.contrast(Color('#fff')) > 7 ? '#fff' : '#000';
  const bookNow = book_now[lng] || book_now.en;

  areThirdPartyCookiesEnabled = true;

  window.addEventListener('message', data => {
    handleIncomingMessage(data);
  });

  const toggleButton = `
  <button id="${PREFIX}__toggle-button"  class="${PREFIX}__toggle-button ${PREFIX}__reset-button" data-guestline-show-panel="true">
    <div class="${PREFIX}__toggle-text" style="background:${primaryColour};color:${primaryTextColour};border-radius:${
    branding.borderRadius
  }px">${bookNow}</div>
  </button>`;

  const DBM_DOMAIN = (window.ENV_DATA || {}).dbmNewUrl || process.env.DBM_NEW_URL;
  const cookieUrlPrefix = `https://${DBM_DOMAIN}/cookies/${collectionId.toUpperCase()}`;

  const panelMode = config.integration.panelFullscreen ? 'fullscreen' : 'slide';
  insertHTML(
    `
    <div id="${PREFIX}__wrapper" class="${PREFIX}__wrapper ${panelMode}">
      ${integration.panelButtonEnabled ? toggleButton : ''}
      <div id="${PREFIX}__backdrop" class="${PREFIX}__backdrop"></div>
      <div id="${PREFIX}" class="${PREFIX} ${panelMode} ${isMobile ? `${PREFIX}__mobile` : ''}">
        <div id="${PREFIX}__container" class="${PREFIX}__container">
          <iframe id="${PREFIX}__dbmIframe" class="${PREFIX}__dbmIframe" allow="web-share"></iframe>
          ${
            isNotChrome()
              ? `<iframe id="${PREFIX}__dbmIframeTestCookies" style="display: none;" src="${cookieUrlPrefix}/testCookies.html" ></iframe>`
              : ''
          }
        </div>
      </div>
    </div>
  `,
    document.body
  );

  triggerEventEmitter.on(
    'panel',
    async ({ show, redirectUrl }: { show: boolean; redirectUrl: string }) => {
      if (show) {
        await showPanel(redirectUrl, config, triggerEventEmitter);
      }
    }
  );

  triggerEventEmitter.on('clicked', async (event: Event) => {
    const showPanelEl = getParentByDataAttribute(
      <Element>event.target,
      'data-guestline-show-panel'
    );

    const isBackdrop =
      event.target instanceof HTMLElement && event.target.id === `${PREFIX}__backdrop`;

    if (isBackdrop) {
      return hidePanel(dbmBaseUrl);
    }

    if (showPanelEl) {
      await showPanel(dbmBaseUrl, config, triggerEventEmitter);

      window.GLAppInsights.trackTrace({
        message: 'Panel - Floating Button Clicked',
        properties: { integration, dbmURL: dbmBaseUrl }
      });
    }
  });
}
